import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { ApiError, type AdminApi } from "@/api";
import { Badge, Banner, Button, Card, Input } from "@/components/ui";
import { commentModeText } from "@/components/test-cases/CriteriaBlock";
import { TC_SOURCE_FEEDBACK_LABEL } from "@/components/test-cases/TestCaseDrawer";
import { isDropped, reactivateDroppedReason } from "@/components/test-cases/dropState";
import { testCaseErrorText, testCaseGate } from "@/components/test-cases/prodCase";
import { cn } from "@/lib/utils";
import type {
  CriteriaVersionList,
  ReferenceList,
  ReferenceListItem,
  ReferenceReviewStatus,
  ReferenceSnapshot,
  ReferenceVersionList,
  TestCaseDetail,
} from "@/types";
import { DomainChip, ReviewBadge } from "./ReferenceBadges";
import { DOMAIN_FILTER_OPTIONS, caseLabel, kstShort, rememberJobIds, rememberedJobIds } from "./referenceCases";
import { REJECT_CONFIRM_TEXT, REVIEW_LABEL, rejectedResultText, reviewStatus } from "./referenceReview";
import { ReferenceVersionViewer, pickVersion } from "./ReferenceVersionViewer";
import { ReferenceScoreBlock } from "./ReferenceScoreBlock";
import { GATE_TONE, compactScores } from "./referenceScores";

const PAGE = 30;
const REVIEW_PAGE_SIZE = 15;
const SEARCH_DELAY_MS = 300;
const STATUS_CHIPS: Array<{ value: ReferenceReviewStatus | ""; label: string; count: keyof ReferenceList["counts"] }> = [
  { value: "not_passed", label: "미통과", count: "not_passed" },
  { value: "passed", label: "통과", count: "passed" },
  { value: "rejected", label: "반려", count: "rejected" },
  { value: "", label: "전체", count: "total" },
];

export const errorText = (err: unknown) =>
  err instanceof ApiError && err.code ? `${err.message} (${err.code})` : err instanceof Error ? err.message : String(err);

export type ReferenceFilters = { q: string; domain: string; env: string };

/** The search box waits a moment so a list is not asked for on every key. */
export function useDebounced<T>(value: T, ms = SEARCH_DELAY_MS): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), ms);
    return () => window.clearTimeout(timer);
  }, [value, ms]);
  return settled;
}

export function ReferenceFilterBar({
  filters,
  onChange,
  children,
}: {
  filters: ReferenceFilters;
  onChange: (next: ReferenceFilters) => void;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2.5">
      <Input
        type="search"
        aria-label="찾기"
        placeholder="질문 또는 답변으로 찾기"
        className="min-w-0 flex-[1_1_200px]"
        value={filters.q}
        onChange={(e) => onChange({ ...filters, q: e.target.value })}
      />
      <select
        aria-label="Category"
        value={filters.domain}
        onChange={(e) => onChange({ ...filters, domain: e.target.value })}
        className="rounded-md border border-border bg-surface-input px-2 py-1.5 text-[12.5px]"
      >
        <option value="">Category 전체</option>
        {DOMAIN_FILTER_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <select
        aria-label="환경"
        value={filters.env}
        onChange={(e) => onChange({ ...filters, env: e.target.value })}
        className="rounded-md border border-border bg-surface-input px-2 py-1.5 text-[12.5px]"
      >
        <option value="">환경 전체</option>
        <option value="dev">dev</option>
        <option value="prod">prod</option>
      </select>
      {children}
    </div>
  );
}

/**
 * `listReferences` for the filters, page by page; `more()` appends the next
 * page. A change of `key` starts over.
 */
export function useReferenceList(api: AdminApi, query: Parameters<AdminApi["listReferences"]>[0], key: string) {
  const [list, setList] = useState<ReferenceList | null>(null);
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let current = true;
    setList(null);
    setError("");
    api.listReferences({ ...query, limit: query?.limit ?? PAGE }).then(
      (got) => current && setList(got),
      (err) => {
        if (!current) return;
        setList(null);
        setError(errorText(err));
      },
    );
    return () => {
      current = false;
    };
    // `key` stands for `query`; an object literal would ask again on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key, version]);

  const more = async () => {
    if (!list?.next_cursor) return;
    setLoadingMore(true);
    try {
      const next = await api.listReferences({ ...query, cursor: list.next_cursor, limit: PAGE });
      setList({ ...next, items: [...list.items, ...next.items] });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoadingMore(false);
    }
  };
  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { list, error, more, loadingMore, reload };
}

/** The user's query the TC replays, and the conversation before it. */
function UserQuery({ detail }: { detail: TestCaseDetail }) {
  const trigger = typeof detail.trigger_message?.content === "string" ? detail.trigger_message.content : detail.trigger_preview;
  const history = (detail.evidence_messages ?? [])
    .filter((m) => m.snapshot_kind === "history")
    .sort((a, b) => a.ordinal - b.ordinal);
  return (
    <div className="grid gap-1">
      <span className="text-[11px] font-semibold text-muted-foreground">사용자 쿼리</span>
      <div className="max-h-40 overflow-y-auto rounded-md bg-muted px-2.5 py-2 text-xs whitespace-pre-wrap">{trigger || "—"}</div>
      {history.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">앞선 대화 {history.length}개</summary>
          <ol className="mt-1 grid max-h-48 gap-1 overflow-y-auto">
            {history.map((m) => (
              <li key={m.evidence_id} className="rounded-md border border-border px-2 py-1 whitespace-pre-wrap">
                <span className="text-[10.5px] text-muted-foreground">{m.sender_email ?? m.role ?? "?"}</span>
                <br />
                {m.content}
              </li>
            ))}
          </ol>
        </details>
      )}
    </div>
  );
}

const CRITERIA_ORIGIN: Record<string, string> = {
  initial: "TC를 만들 때 👎 의견으로 만든 기준",
  manual_regeneration: "👎 의견을 모아 다시 만든 기준",
};

/**
 * The 👎 the user left on the original answer, and how they became the
 * criteria this TC is scored by (the current criteria version's origin).
 */
function FeedbackAndCriteria({ detail, versions }: { detail: TestCaseDetail; versions: CriteriaVersionList | null }) {
  const feedbacks = detail.answer_feedbacks ?? detail.feedbacks.map((f) => ({ ...f, is_tc_source: f.feedback_id === detail.source_feedback_id }));
  const current = versions?.items.find((v) => v.version === versions.current_version) ?? null;
  const checks = detail.criteria?.checks ?? [];
  return (
    <div className="grid gap-2.5">
      <div className="grid gap-1">
        <span className="text-[11px] font-semibold text-muted-foreground">사용자 피드백 (👎 {feedbacks.length})</span>
        {feedbacks.length === 0 ? (
          <p className="text-xs text-muted-foreground">받아 둔 👎가 없습니다.</p>
        ) : (
          <ul className="grid max-h-48 gap-1 overflow-y-auto" aria-label="사용자 피드백">
            {feedbacks.map((f) => (
              <li key={f.feedback_id} className="grid gap-0.5 rounded-md border border-border px-2 py-1.5 text-xs">
                <span className="flex flex-wrap items-center gap-1 text-[10.5px] text-muted-foreground">
                  {f.is_tc_source && (
                    <Badge size="compact" variant="accent">
                      {TC_SOURCE_FEEDBACK_LABEL}
                    </Badge>
                  )}
                  {f.categories.map((c) => (
                    <Badge key={c} size="compact">
                      {c}
                    </Badge>
                  ))}
                  <span className="tabular-nums">{kstShort(f.created_at)}</span>
                </span>
                <p className="whitespace-pre-wrap">{f.comment || <span className="text-muted-foreground">코멘트 없음</span>}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="grid gap-1">
        <span className="text-[11px] font-semibold text-muted-foreground">이 피드백으로 만든 채점 기준</span>
        {current ? (
          <p className="text-[11.5px] text-muted-foreground">
            기준 v{current.version} · {CRITERIA_ORIGIN[current.origin] ?? "기준"}
            {current.feedback_count != null && ` · 👎 ${current.feedback_count}건 반영`}
            {commentModeText(current.comment_mode) && ` · ${commentModeText(current.comment_mode)}`}
            {` · ${kstShort(current.created_at)}`}
            {current.created_by && ` · ${current.created_by}`}
          </p>
        ) : (
          <p className="text-[11.5px] text-muted-foreground">이 기준이 어떤 👎로 만들어졌는지 남은 기록이 없습니다.</p>
        )}
        {checks.length ? (
          <ol className="grid list-decimal gap-0.5 pl-4.5 text-xs">
            {checks.map((c, i) => (
              <li key={i}>
                {c.title || c.criterion}
                {c.score_impact != null && <span className="text-muted-foreground"> · 가중치 {c.score_impact}</span>}
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-xs text-muted-foreground">채점 기준이 없습니다.</p>
        )}
      </div>
    </div>
  );
}

type Decision = "passed" | "note" | "rejected";

/**
 * 통과 (with an optional note), a note alone or 반려 (its reason is the note)
 * for one version, and 다시 만들기 for its TC. A note is the reviewer's own:
 * it is not a rubric, does not move scoring and is not sent to a regeneration.
 * A rejection clears the version's approval; it asks first, since the answer
 * names the version scoring reads afterwards only once it is sent.
 */
function ReviewForm({
  api,
  snapshot,
  onReviewed,
}: {
  api: AdminApi;
  snapshot: ReferenceSnapshot;
  onReviewed: (decision: Decision) => void;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState<"" | Decision | "regenerate">("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    setNote("");
    setError("");
    setNotice("");
    setConfirming(false);
  }, [snapshot.snapshot_id]);

  const passed = reviewStatus(snapshot) === "passed";
  const text = note.trim();

  const submit = async (decision: Decision) => {
    setBusy(decision);
    setError("");
    setNotice("");
    try {
      const out = await api.reviewReference(snapshot.snapshot_id, text ? { decision, comment: text } : { decision });
      setNote("");
      setConfirming(false);
      if (decision === "rejected") setNotice(`v${snapshot.version}을 반려했습니다. ${rejectedResultText(out.active_version ?? null)}`);
      onReviewed(decision);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy("");
    }
  };

  const regenerate = async () => {
    setBusy("regenerate");
    setError("");
    setNotice("");
    try {
      const job = await api.requestReference(snapshot.test_case_id);
      rememberJobIds([job.job_id, ...rememberedJobIds()]);
      setNotice("새 버전 생성을 요청했습니다. 진행 상황은 생성 탭에서 볼 수 있습니다.");
    } catch (err) {
      if (err instanceof ApiError && err.code === "REFERENCE_JOB_ACTIVE") {
        setNotice("이 TC는 이미 생성 중입니다. 진행 상황은 생성 탭에서 볼 수 있습니다.");
      } else {
        setError(errorText(err));
      }
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="grid gap-2 border-t border-border pt-3">
      <p className="text-[12px] text-muted-foreground">
        {passed
          ? snapshot.active
            ? `v${snapshot.version}은 통과했고 지금 채점 기준입니다.`
            : `v${snapshot.version}은 통과했지만 지금은 다른 버전으로 채점합니다. 다시 통과하면 이 버전으로 채점합니다.`
          : reviewStatus(snapshot) === "rejected"
            ? `v${snapshot.version}은 반려했습니다. 다시 통과하면 이 버전으로 채점합니다.`
            : `v${snapshot.version}은 미통과입니다. 통과를 눌러야 채점 기준이 됩니다.`}
      </p>
      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}
      {confirming && (
        <Banner tone="error">
          <span className="grid gap-1.5">
            <span>{REJECT_CONFIRM_TEXT}</span>
            <span className="flex justify-end gap-1.5">
              <Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => setConfirming(false)}>
                취소
              </Button>
              <Button size="sm" disabled={Boolean(busy) || !text} onClick={() => void submit("rejected")}>
                {busy === "rejected" && <Loader2 className="animate-spin" />}
                반려 확정
              </Button>
            </span>
          </span>
        </Banner>
      )}
      <textarea
        aria-label="관리자 피드백"
        placeholder="관리자 피드백 (선택, 반려에는 사유로 필수). 기록으로만 남고 채점·재생성에는 쓰이지 않습니다."
        className="min-h-16 rounded-md border border-border bg-surface-input px-2.5 py-1.5 text-[12.5px]"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <div className="flex flex-wrap justify-end gap-1.5">
        <Button size="sm" disabled={Boolean(busy) || (passed && snapshot.active)} onClick={() => void submit("passed")}>
          {busy === "passed" && <Loader2 className="animate-spin" />}
          {passed && !snapshot.active ? "다시 통과" : "통과"}
        </Button>
        <Button size="sm" variant="outline" disabled={Boolean(busy) || !text} onClick={() => void submit("note")}>
          {busy === "note" && <Loader2 className="animate-spin" />}
          피드백 저장
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={Boolean(busy) || !text || confirming}
          title={text ? undefined : "반려 사유를 관리자 피드백 칸에 적어 주세요."}
          onClick={() => setConfirming(true)}
        >
          반려
        </Button>
        <Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => void regenerate()}>
          {busy === "regenerate" && <Loader2 className="animate-spin" />}
          다시 만들기
        </Button>
      </div>
    </div>
  );
}

/**
 * The TC's 활성/비활성, saved through the TC PATCH (the same as All Test
 * Cases). Shown as saved only once the server answered; a refusal (409 for
 * a prod TC without prod access, a dropped TC) keeps the server's value and
 * says why. Its message is its own: it never overwrites the review form's.
 */
function ActiveToggle({
  api,
  detail,
  onSaved,
}: {
  api: AdminApi;
  detail: TestCaseDetail;
  onSaved: (next: TestCaseDetail) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  // A dropped TC can still be made inactive, never active again (BE answers 409 TC_DROPPED).
  const gate = isDropped(detail) && !detail.is_active
    ? { allowed: false as const, reason: reactivateDroppedReason(detail.drop_reason_code) }
    : testCaseGate(detail, "edit");

  const toggle = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    try {
      const next = await api.patchTestCase(detail.test_case_id, { is_active: !detail.is_active });
      onSaved(next);
      setNotice(next.is_active ? "활성으로 바꿨습니다." : "비활성으로 바꿨습니다.");
    } catch (err) {
      setError(testCaseErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-1" aria-label="TC 활성 상태">
      <div className="flex flex-wrap items-center gap-2 text-[12px]">
        <button
          type="button"
          role="switch"
          aria-checked={detail.is_active}
          aria-label="TC 활성"
          disabled={busy || !gate.allowed}
          title={gate.reason ?? undefined}
          onClick={() => void toggle()}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 disabled:opacity-50",
            detail.is_active ? "border-primary bg-accent text-accent-foreground" : "border-border bg-card text-muted-foreground",
          )}
        >
          {busy && <Loader2 className="size-3.5 animate-spin" />}
          {detail.is_active ? "활성" : "비활성"}
        </button>
        <span className="text-muted-foreground">비활성 TC는 루프 대상이 아닙니다.</span>
      </div>
      {!gate.allowed && gate.reason && <p className="text-[11.5px] text-muted-foreground">{gate.reason}</p>}
      {error && <Banner tone="error">활성 상태를 저장하지 못했습니다: {error}</Banner>}
      {notice && <Banner tone="success">{notice}</Banner>}
    </div>
  );
}

/** One TC open for review: its question, criteria, every version and the form. */
function ReviewPanel({
  api,
  testCaseId,
  snapshotId,
  onSelectVersion,
  onReviewed,
  scoresOf,
}: {
  api: AdminApi;
  testCaseId: string;
  snapshotId: string | null;
  onSelectVersion: (snapshotId: string) => void;
  onReviewed: (snapshotId: string, decision: Decision) => void;
  /** The list's scores of a version (absent when the version is not on this page). */
  scoresOf: (snapshotId: string) => ReferenceListItem["scores"] | undefined;
}) {
  const [detail, setDetail] = useState<TestCaseDetail | null>(null);
  const [versions, setVersions] = useState<ReferenceVersionList | null>(null);
  const [criteria, setCriteria] = useState<CriteriaVersionList | null>(null);
  const [error, setError] = useState("");
  const [round, setRound] = useState(0);

  useEffect(() => {
    let current = true;
    setError("");
    setDetail(null);
    setCriteria(null);
    api.getTestCase(testCaseId).then(
      (d) => {
        if (!current) return;
        setDetail(d);
        // How the criteria came from the 👎 is a hint: without it the checks still show.
        api.listTestCaseCriteriaVersions(testCaseId, d.source_environment).then(
          (c) => current && setCriteria(c),
          () => current && setCriteria(null),
        );
      },
      (err) => current && setError(`TC를 읽지 못했습니다: ${errorText(err)}`),
    );
    return () => {
      current = false;
    };
  }, [api, testCaseId]);

  useEffect(() => {
    let current = true;
    api.listReferenceVersions(testCaseId).then(
      (v) => current && setVersions(v),
      (err) => current && setError(`버전을 읽지 못했습니다: ${errorText(err)}`),
    );
    return () => {
      current = false;
    };
  }, [api, testCaseId, round]);

  const selected = versions ? pickVersion(versions, snapshotId) : null;

  return (
    <Card className="flex min-w-0 flex-col xl:absolute xl:inset-0 xl:overflow-hidden">
      <div tabIndex={0} className="grid min-h-0 flex-1 content-start gap-3 overflow-y-auto px-3.5 py-3 text-[12.5px]">
        {error && <Banner tone="error">{error}</Banner>}
        <div className="grid gap-1">
          <h2 className="text-[14px] font-semibold">{detail ? caseLabel(detail) : "TC를 읽는 중…"}</h2>
          <p className="flex flex-wrap items-center gap-1.5 font-mono text-[11px] break-all text-muted-foreground">
            {testCaseId}
            {detail && (
              <>
                <span className="font-sans">· {detail.source_environment}</span>
                <DomainChip domain={detail.domain} />
              </>
            )}
          </p>
        </div>
        {detail && <ActiveToggle api={api} detail={detail} onSaved={setDetail} />}
        {detail && <UserQuery detail={detail} />}
        {detail && (
          <details className="rounded-md border border-border px-2.5 py-2">
            <summary className="cursor-pointer text-xs text-muted-foreground">사용자 피드백·채점 기준 보기</summary>
            <div className="mt-2"><FeedbackAndCriteria detail={detail} versions={criteria} /></div>
          </details>
        )}
        {!versions ? (
          !error && <p className="text-muted-foreground">버전을 읽는 중…</p>
        ) : (
          <ReferenceVersionViewer
            versions={versions}
            selectedId={selected?.snapshot_id ?? null}
            onSelect={onSelectVersion}
            bodyClassName="h-[40vh] min-h-72 max-h-[36rem] px-4 py-3 text-sm leading-7"
          />
        )}
        {selected && (
          <ReferenceScoreBlock scores={scoresOf(selected.snapshot_id)} />
        )}
      </div>
      {selected && (
        <div className="shrink-0 bg-card px-3.5 pb-3">
          <ReviewForm
            api={api}
            snapshot={selected}
            onReviewed={(decision) => {
              setRound((r) => r + 1);
              onReviewed(selected.snapshot_id, decision);
            }}
          />
        </div>
      )}
    </Card>
  );
}

/**
 * Test Cases → Reference Answer → 검토. Versions across TCs, newest first,
 * the 미통과 ones in view first; a person reads one against the user's query,
 * the user's 👎 and the criteria made from them, and passes it (it becomes
 * what scoring reads) — a note may go with it or alone. Contract:
 * sams-reference-api.md §5–6.
 */
export function ReferenceReviewTab({
  api,
  reloadKey,
  focus,
  onFocus,
}: {
  api: AdminApi;
  reloadKey: number;
  /** The TC (and version) open on the right, from the URL. */
  focus: { tc: string | null; snap: string | null };
  onFocus: (tc: string, snap: string | null) => void;
}) {
  const [status, setStatus] = useState<ReferenceReviewStatus | "">("not_passed");
  const [filters, setFilters] = useState<ReferenceFilters>({ q: "", domain: "", env: "" });
  const [page, setPage] = useState(1);
  const [cursors, setCursors] = useState<Array<string | null>>([null]);
  const q = useDebounced(filters.q.trim());
  const cursor = cursors[page - 1];
  const query = useMemo(
    () => ({
      review_status: status || undefined,
      source_environment: filters.env || undefined,
      domain: filters.domain || undefined,
      q: q || undefined,
      limit: REVIEW_PAGE_SIZE,
      cursor,
    }),
    [status, filters.env, filters.domain, q, cursor],
  );
  const { list, error, reload } = useReferenceList(api, query, `${JSON.stringify(query)}|${reloadKey}`);
  const items = list?.items ?? [];
  const total = list?.counts[status || "total"] ?? 0;
  const pages = Math.max(1, Math.ceil(total / REVIEW_PAGE_SIZE));

  useEffect(() => {
    if (list && page > pages) setPage(pages);
  }, [list, page, pages]);

  // Nothing chosen yet: the first row is open, without writing it into history.
  const open: { tc: string; snap: string | null } | null = focus.tc
    ? { tc: focus.tc, snap: focus.snap }
    : items[0]
      ? { tc: items[0].test_case_id, snap: items[0].snapshot_id }
      : null;

  const keepOpen = () => {
    if (open && !focus.tc) onFocus(open.tc, open.snap);
  };
  const resetPage = () => {
    keepOpen();
    setPage(1);
    setCursors([null]);
  };
  const changePage = (target: number) => {
    if (!list || (target > page && !list.next_cursor)) return;
    keepOpen();
    if (target > page) setCursors([...cursors.slice(0, page), list.next_cursor]);
    setPage(target);
  };

  const next = (after: string): ReferenceListItem | undefined => {
    const index = items.findIndex((i) => i.snapshot_id === after);
    return items.slice(index + 1).find((i) => i.snapshot_id !== after);
  };

  return (
    <div className="grid gap-4.5">
      <div className="flex flex-wrap gap-2" role="group" aria-label="검토 상태로 거르기">
        {STATUS_CHIPS.map(({ value, label, count }) => (
          <button
            key={value || "all"}
            type="button"
            aria-pressed={status === value}
            onClick={() => { setStatus(value); resetPage(); }}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[12.5px]",
              status === value && "border-primary bg-accent text-accent-foreground",
            )}
          >
            {label} <b className="font-semibold tabular-nums">{list ? list.counts[count] : "–"}</b>
          </button>
        ))}
      </div>
      {error && <Banner tone="error">Reference 목록을 읽지 못했습니다: {error}</Banner>}

      <div className="grid items-start gap-4.5 xl:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <Card className="min-w-0">
          <ReferenceFilterBar filters={filters} onChange={(next) => { setFilters(next); resetPage(); }} />
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-[13px]">
              <thead>
                <tr className="border-b border-border text-left text-[11.5px] text-muted-foreground">
                  <th className="px-2.5 py-2 font-medium">질문</th>
                  <th className="px-2.5 py-2 font-medium whitespace-nowrap">버전</th>
                  <th className="px-2.5 py-2 font-medium whitespace-nowrap">점수</th>
                  <th className="px-2.5 py-2 font-medium">검토</th>
                </tr>
              </thead>
              <tbody>
                {!list && !error && (
                  <tr>
                    <td colSpan={4} className="px-3 py-5 text-muted-foreground">
                      <Loader2 className="mr-1.5 inline size-4 animate-spin" />
                      목록을 읽는 중…
                    </td>
                  </tr>
                )}
                {list && items.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-5 text-muted-foreground">
                      {status === "not_passed" ? "미통과 버전이 없습니다." : "조건에 맞는 버전이 없습니다."}
                    </td>
                  </tr>
                )}
                {items.map((item) => {
                  const on = open?.snap === item.snapshot_id || (open?.tc === item.test_case_id && !open.snap);
                  return (
                    <tr key={item.snapshot_id} className={cn("border-b border-border last:border-0 hover:bg-accent-hover", on && "bg-accent")}>
                      <td className="max-w-0 px-2.5 py-2 md:w-[62%]">
                        <button
                          type="button"
                          className="block w-full truncate text-left font-medium hover:underline"
                          title={item.test_case.trigger_preview ?? undefined}
                          onClick={() => onFocus(item.test_case_id, item.snapshot_id)}
                        >
                          {item.test_case.title || item.test_case.trigger_preview || item.test_case_id.slice(0, 8)}
                        </button>
                        <span className="text-[11px] text-muted-foreground tabular-nums">
                          {item.test_case.source_environment} · {kstShort(item.created_at)}
                        </span>
                      </td>
                      <td className="px-2.5 py-2 whitespace-nowrap tabular-nums">
                        v{item.version} / {item.version_count}
                        {item.active && <div className="text-[10.5px] text-muted-foreground">현재 기준</div>}
                      </td>
                      <td className="px-2.5 py-2 text-[11.5px] whitespace-nowrap tabular-nums">
                        {compactScores(item.scores) ?? <span className="text-muted-foreground">채점 전</span>}
                        {item.scores?.gates.flagged && (
                          <Badge size="compact" className={cn("mt-0.5 block", GATE_TONE.flag)}>
                            탈락 예정
                          </Badge>
                        )}
                      </td>
                      <td className="px-2.5 py-2">
                        <ReviewBadge status={reviewStatus(item)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="grid gap-2 border-t border-border px-3 py-2.5 text-[11.5px]">
            <div className="flex justify-end">
              {list && <span className="text-muted-foreground tabular-nums">{items.length ? (page - 1) * REVIEW_PAGE_SIZE + 1 : 0}–{(page - 1) * REVIEW_PAGE_SIZE + items.length} / {total}개</span>}
            </div>
            <nav className="flex items-center justify-between gap-2" aria-label="TC 목록 페이지">
              <Button variant="outline" size="sm" aria-label="이전 TC 페이지" disabled={!list || page === 1} onClick={() => changePage(page - 1)}>이전</Button>
              <span className="text-muted-foreground tabular-nums">{page} / {list ? pages : "–"}</span>
              <Button variant="outline" size="sm" aria-label="다음 TC 페이지" disabled={!list?.next_cursor} onClick={() => changePage(page + 1)}>다음</Button>
            </nav>
          </div>
        </Card>

        <div className="min-w-0 xl:relative xl:self-stretch">
          {open ? (
            <ReviewPanel
              key={open.tc}
              api={api}
              testCaseId={open.tc}
              snapshotId={open.snap}
              onSelectVersion={(snap) => onFocus(open.tc, snap)}
              onReviewed={(snap, decision) => {
                // In the 미통과 queue a pass opens the next version; a note keeps this one open.
                const following = status === "not_passed" && decision === "passed" ? next(snap) : undefined;
                if (following) onFocus(following.test_case_id, following.snapshot_id);
                reload();
              }}
              scoresOf={(snap) => items.find((i) => i.snapshot_id === snap)?.scores}
            />
          ) : (
            list && (
              <Card className="px-3.5 py-3 text-[12.5px] text-muted-foreground xl:absolute xl:inset-0 xl:overflow-y-auto">
                {REVIEW_LABEL.not_passed} 버전이 생기면 여기서 열립니다.
              </Card>
            )
          )}
        </div>
      </div>
    </div>
  );
}
